import {
  agDepartmentsCol,
  agDetailCol,
  agEndDateCol,
  agInvoicePaidCol,
  agInvoiceReceivedCol,
  agOrderPlacedCol,
  agPriceCol,
  agSelectCol,
  agStartDateCol,
  agStatusCol,
  licenseCol,
  exchangeRateCol,
  priceChangeCol
} from '@/ag-grid/ag-columns'
import { notAnyPriceCheck, notAnyValueCheck } from '@/ag-grid/utils/notAnyValue'
import { dropdown } from '@/ag-grid/utils/icons-as-text'
import { getProp, metaValuesRenderer } from '@lib/generic.utils'
import { isEqual } from 'lodash'
import * as shared from '@/modules/list-view-subscriptions/shared/ag-grid-columns'

export default {
  computed: {
    columnDefs() {
      return [
        agSelectCol({ vuexModule: 'lv-subscriptions-edit-mode' }),

        agStatusCol(),

        shared.subscriptionsCol(),

        {
          ...shared.productTypeCol(),
          cellRenderer: params =>
            `${notAnyValueCheck({ ...params, value: params.data.product_type?.name })} ${dropdown}`,
          editable: true,
          cellClass: ['editable-cell'],
          cellEditorFramework: 'DropdownEditor',
          cellEditorParams: params => {
            return {
              optionsModule: 'lv-subscriptions',
              optionsState: 'productTypes',
              optionLabel: 'name',
              optionValue: 'id',
              withNA: true,
              getValue: model => {
                if (model === null) return null
                if (params.value?.id === model.id) return params.value
                return model.id
              }
            }
          }
        },

        {
          ...shared.publisherCol(),
          cellRenderer: params => `${notAnyValueCheck({ ...params, value: params.data.publisher?.name })} ${dropdown}`,
          editable: true,
          cellClass: ['editable-cell'],
          cellEditorFramework: 'DropdownEditor',
          cellEditorParams: params => {
            return {
              optionsModule: 'lv-subscriptions',
              optionsState: 'publishers',
              optionLabel: 'name',
              optionValue: 'id',
              getValue: model => {
                if (params.value.id === model.id) return params.value
                return model.id
              }
            }
          }
        },

        {
          ...shared.vendorCol(),
          cellRenderer: params => `${notAnyValueCheck({ ...params, value: params.data.vendor?.name })} ${dropdown}`,
          editable: true,
          cellClass: ['editable-cell'],
          cellEditorFramework: 'DropdownEditor',
          cellEditorParams: params => {
            return {
              optionsModule: 'lv-subscriptions-edit-mode',
              optionsState: 'vendors',
              optionLabel: 'name',
              optionValue: 'id',
              withNA: true,
              getValue: model => {
                if (model === null) return null
                if (params.value?.id === model.id) return params.value
                return model.id
              }
            }
          }
        },

        agStartDateCol({
          ...shared.cancellationDeadlineCol(),
          editable: true,
          cellClass: ['editable-cell'],
          cellEditorFramework: 'DateEditor'
        }),

        agStartDateCol({
          editable: true,
          cellClass: ['editable-cell'],
          cellEditorFramework: 'DateEditor'
        }),

        agEndDateCol({
          editable: true,
          cellClass: ['editable-cell'],
          cellEditorFramework: 'DateEditor'
        }),

        agDetailCol({
          hide: false,
          editable: true,
          cellClass: ['editable-cell'],
          cellEditorFramework: 'TextEditor'
        }),

        {
          ...shared.publisherReferenceCol(),
          editable: true,
          cellClass: ['editable-cell'],
          cellEditorFramework: 'TextEditor'
        },

        {
          ...shared.libraryReferenceCol(),
          editable: true,
          cellClass: ['editable-cell'],
          cellEditorFramework: 'TextEditor'
        },

        {
          ...shared.licenseManagerCol(),
          cellRenderer: params => `${notAnyValueCheck({ ...params, value: params.data.owner?.name })} ${dropdown}`,
          editable: true,
          cellClass: ['editable-cell'],
          cellEditorFramework: 'DropdownEditor',
          cellEditorParams: params => {
            return {
              optionsModule: 'lv-subscriptions-edit-mode',
              optionsState: 'owners',
              optionLabel: 'name',
              optionValue: 'id',
              getValue: model => {
                if (params.value.id === model.id) return params.value
                return model.id
              }
            }
          }
        },

        agPriceCol('List Price', 'list_price', {
          hide: true,
          editable: true,
          cellClass: ['editable-cell', 'ag-right-aligned-cell']
        }),

        agPriceCol('First Offer Price', 'first_offer_price', {
          hide: true,
          editable: true,
          cellClass: ['editable-cell', 'ag-right-aligned-cell']
        }),

        agPriceCol('Forecasted Price', 'forecasted_price', {
          hide: true,
          editable: true,
          cellClass: ['editable-cell', 'ag-right-aligned-cell']
        }),

        agPriceCol('Forecasted Local Price', 'forecasted_local_price', {
          hide: true,
          cellRenderer: params => notAnyPriceCheck(params, 'normalized_currency'),
          editable: true,
          cellClass: ['editable-cell', 'ag-right-aligned-cell']
        }),

        agPriceCol('Price', 'price', {
          editable: params => params.data.items_count === 0,
          cellClassRules: {
            'editable-cell': params => params.data.items_count === 0,
            'ag-right-aligned-cell': params => true
          }
        }),

        agPriceCol('Local Price', 'normalized_price', {
          cellRenderer: params => notAnyPriceCheck(params, 'normalized_currency'),
          editable: params => params.data.items_count === 0,
          cellClassRules: {
            'editable-cell': params => params.data.items_count === 0,
            'ag-right-aligned-cell': params => true
          }
        }),

        exchangeRateCol({
          hide: true,
          editable: params => params.data.items_count === 0,
          cellClass: [],
          cellClassRules: {
            'editable-cell': params => params.data.items_count === 0,
            'ag-right-aligned-cell': params => true
          }
        }),

        agOrderPlacedCol(),

        agInvoiceReceivedCol(),

        agInvoicePaidCol(),

        licenseCol(),

        {
          ...shared.stakeholdersCol(),
          cellRenderer: params => {
            if (!params.value.length) return `N/A ${dropdown}`

            const names = params.value.map(val => val.name)
            return `(${params.value.length}) ${names.join(',')} ${dropdown}`
          },
          editable: true,
          cellClass: ['editable-cell'],
          cellEditorFramework: 'DropdownEditor',
          cellEditorParams: params => ({
            optionsModule: 'lv-subscriptions',
            optionsState: 'stakeholders',
            optionLabel: 'name',
            optionValue: 'id',
            initialValue: params.value.map(val => val.id),
            emitValue: true,
            getValue: model => {
              const value = params.value.map(v => v.id)

              if (isEqual(value, model)) return params.value

              const stakeholders = this.$store.state['lv-subscriptions'].stakeholders

              return [...stakeholders.filter(val => model.includes(val.id))]
            },
            multiple: true
          })
        },

        agPriceCol('Usage', 'usage', {
          hide: true,
          editable: true,
          cellRenderer: notAnyValueCheck,
          cellClass: ['editable-cell', 'ag-right-aligned-cell']
        }),

        agPriceCol('CPU Original Price', 'cost_per_use_original', {
          hide: true,
          maxWidth: 120
        }),

        agPriceCol('CPU Local Price', 'cost_per_use_local', {
          cellRenderer: params => notAnyPriceCheck(params, 'normalized_currency'),
          maxWidth: 100,
          hide: true
        }),

        agDepartmentsCol(
          'lv-subscriptions',
          {
            headerName: this.departmentLabel.plural,
            editable: true,
            cellRenderer: params => {
              if (!params.value.length) return `N/A ${dropdown}`

              const depNames = params.value.map(dep => dep.name)
              return `(${params.value.length}) ${depNames.join(',')} ${dropdown}`
            },
            cellClass: ['editable-cell'],
            hide: true,
            cellEditorFramework: 'DropdownEditor',
            cellEditorParams: params => ({
              optionsModule: 'lv-subscriptions',
              optionsState: 'departments',
              optionLabel: 'name',
              optionValue: 'id',
              initialValue: params.value.map(department => department.id),
              emitValue: true,
              getValue: model => {
                const value = params.value.map(v => v.id)

                if (isEqual(value, model)) return params.value

                const deps = this.$store.state['lv-subscriptions'].departments

                return [...deps.filter(dep => model.includes(dep.id))]
              },
              multiple: true
            })
          },
          {
            optionsModule: 'lv-subscriptions'
          }
        ),

        shared.costCentersCol(),

        shared.financialPeriod(),

        priceChangeCol()
      ]
    },

    metadataColumnDefs() {
      return (this.metadataColumns || []).reduce((acc, curr) => {
        const field = `meta_data.${curr.system_name}`
        const colDef = {
          headerName: curr.name,
          field,
          type: ['textFilterColumn'],
          cellRenderer: params => {
            params.value = params.data['meta_data'][curr.system_name]
            return metaValuesRenderer(params)
          },
          cellClass: ['editable-cell'],
          editable: true,
          hide: true,
          ...this.getEditableOptions(curr)
        }

        acc.push(colDef)

        return acc
      }, [])
    },

    gridOptions() {
      return {
        columnDefs: [...this.columnDefs, ...this.metadataColumnDefs],

        defaultColDef: {
          floatingFilter: true
        },

        rowSelection: 'multiple',
        suppressRowClickSelection: true,
        rowModelType: 'serverSide',
        serverSideStoreType: 'partial',
        cacheBlockSize: 40
      }
    }
  },

  methods: {
    getEditableOptions(metadata) {
      if (metadata?.type?.name === 'select') return this.dropdownOptions(metadata)
      else if (['text', 'textarea'].includes(metadata?.type?.name)) return { cellEditorFramework: 'TextEditor' }
      else if (metadata?.type?.name === 'checkbox') return this.checkboxOptions(metadata)

      return {}
    },

    dropdownOptions(metadata) {
      return {
        cellEditorFramework: 'DropdownEditor',
        cellEditorParams: {
          options: [...metadata.value_options],
          multiple: true,
          getValue(model) {
            return model
          }
        }
      }
    },

    checkboxOptions(metadata) {
      return {
        filter: 'CustomFilter',
        floatingFilterComponentFramework: 'DropdownFilter',
        filterParams: {
          filterType: 'boolean',
          type: 'contains',
          options: [
            { label: 'Yes', value: 'yes' },
            { label: 'No', value: 'no' }
          ]
        },
        cellEditorFramework: 'DropdownEditor',
        cellEditorParams: {
          options: [
            ...(metadata.is_required ? [] : [{ label: 'N/A', value: null }]),
            { label: 'Yes', value: true },
            { label: 'No', value: false }
          ],
          emitValue: true,
          mapOptions: true
        }
      }
    }
  }
}
